// @ts-strict-ignore
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MultiSelect, TextField } from '@usealloy/component-library';
import type { Option, SelectedOption } from '@usealloy/component-library';
import { MULTISELECT_PROPS } from 'flow/editor/components/constants';
import useWorkflowVersionSchemaFields from '~/dashboard/views/workflows/api/useWorkflowVersionSchemaFields';
import type { SchemaField } from '~/helpers/api-new/modules/workflows/workflowsApi';
import { PLACEHOLDER_DIFF_STATUS, withDiffStyles } from '../../diffStyles';
import { selectDiffMode } from 'flow/store/redux/selectors';
import { useAppSelector } from '~/store/reduxHooks';

// This level's own segment of a saved path, plus the raw remainder to fall back to whole when
// `name` doesn't match a known field (a free-typed value can have dots or an array index).
function getSegmentAtLevel(
  fullPath: string,
  prefixConsumed: string,
): { name: string; remainder: string } | null {
  const consumesSameBranch = fullPath === prefixConsumed || fullPath.startsWith(`${prefixConsumed}.`);
  const remainder = prefixConsumed && consumesSameBranch
    ? fullPath.slice(prefixConsumed.length + 1)
    : fullPath;
  if (!remainder) return null;

  return { name: remainder.split('.')[0], remainder };
}

const isDrillable = (field?: SchemaField): boolean => (
  field?.kind === 'object' || field?.kind === 'array'
);

function SchemaFieldLevel({
  workflowToken,
  internalName,
  pathPrefix,
  composedPrefix,
  onComposePath,
  initialValue,
  prefetchedRootFields,
}: {
  workflowToken: string;
  internalName: string;
  pathPrefix?: string;
  composedPrefix: string;
  onComposePath: (path: string) => void;
  initialValue?: string;
  // Root fields are already fetched by DestinationFieldPicker - reused instead of refetching.
  prefetchedRootFields?: SchemaField[];
}) {
  const [selectedField, setSelectedField] = useState<SchemaField | null>(null);
  const isInDiffMode = useAppSelector(selectDiffMode);

  const { data } = useWorkflowVersionSchemaFields({
    workflowToken,
    internalName,
    pathPrefix,
    enabled: !prefetchedRootFields,
  });
  const fields = prefetchedRootFields ?? data?.fields ?? [];

  useEffect(() => {
    if (selectedField || !initialValue || fields.length === 0) return;

    const segment = getSegmentAtLevel(initialValue, composedPrefix);
    if (!segment) return;

    const matchedField = fields.find(field => field.name === segment.name);
    if (matchedField) {
      setSelectedField(matchedField);
      return;
    }

    setSelectedField({ name: segment.remainder, kind: 'primitive' });
    // Only run once fields become available, not on every subsequent user selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.length]);

  const composeAndEmit = (field: SchemaField) => {
    onComposePath(composedPrefix ? `${composedPrefix}.${field.name}` : field.name);
  };

  const onSelect = (selected: SelectedOption<SchemaField>) => {
    const option = selected as Option<SchemaField>;
    setSelectedField(option.meta);
    // An array is a valid destination on its own (route the whole array), so emit it right away
    // and still allow drilling; an object only becomes a path once a leaf below it is picked.
    if (option.meta.kind !== 'object') {
      composeAndEmit(option.meta);
    }
  };

  // Lets the user type any field name - the schema list is a convenience, not a restriction.
  // An array index is typed inline (`phones[2]`) rather than picked; a bare array name routes
  // the whole array.
  const onCreateOption = (newOption: Option) => {
    const typedField: SchemaField = { name: newOption.value, kind: 'primitive' };
    setSelectedField(typedField);
    composeAndEmit(typedField);
  };

  const joinSegment = (prefix: string, segment: string) => (prefix ? `${prefix}.${segment}` : segment);
  const nextPrefix = selectedField ? joinSegment(pathPrefix, selectedField.name) : null;
  const nextComposedPrefix = selectedField ? joinSegment(composedPrefix, selectedField.name) : null;

  return (
    <>
      <MultiSelect
        // TODO: hardcoded until the per-field diff status is wired in. The diff-mode guard is
        // redundant once that lookup returns null outside diff mode, and can be dropped then.
        {...withDiffStyles(MULTISELECT_PROPS, isInDiffMode ? PLACEHOLDER_DIFF_STATUS : null)}
        openMenuOnFocus
        isCreatable
        onCreateOption={onCreateOption}
        options={fields.map(field => ({ label: field.name, value: field.name, meta: field }))}
        selectedOptions={selectedField
          ? { label: selectedField.name, value: selectedField.name }
          : null}
        placeholder="Payload Field"
        onChange={onSelect}
      />
      {isDrillable(selectedField) && (
        <SchemaFieldLevel
          // Keyed on name so switching fields remounts instead of reusing stale state.
          key={selectedField.name}
          workflowToken={workflowToken}
          internalName={internalName}
          pathPrefix={nextPrefix}
          composedPrefix={nextComposedPrefix}
          onComposePath={onComposePath}
          initialValue={initialValue}
        />
      )}
    </>
  );
}

// Cascading destination-field picker. customListFields skips fetching for Custom List targets;
// falls back to free text when there's no schema data either way.
export default function DestinationFieldPicker({
  internalName,
  value,
  onChange,
  customListFields,
}: {
  internalName: string;
  value: string;
  onChange: (value: string) => void;
  customListFields?: SchemaField[];
}) {
  const { workflowToken } = useParams();
  const isCustomList = customListFields !== undefined;
  const rootQuery = useWorkflowVersionSchemaFields({
    workflowToken, internalName, enabled: !isCustomList,
  });

  const fields = isCustomList ? customListFields : (rootQuery.data?.fields ?? []);
  if (fields.length === 0) {
    return (
      <TextField
        label="Payload Field"
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    );
  }

  return (
    <SchemaFieldLevel
      workflowToken={workflowToken}
      internalName={internalName}
      pathPrefix={undefined}
      composedPrefix=""
      onComposePath={onChange}
      initialValue={value}
      prefetchedRootFields={fields}
    />
  );
}
